import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GlassTitle } from "../components/GlassTitle";
import { toast } from "sonner";
import {
  getPublicPricing,
  getSubscriptionCurrent,
  verifySubscription,
  createSubscriptionCheckout,
  createBillingPortal,
} from "../services/api.broker";
import {
  CheckCircle2,
  Crown,
  Zap,
  Shield,
  Sparkles,
  Loader2,
  CreditCard,
  AlertCircle,
  ArrowRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import PlatformShell from "../components/PlatformShell";
import { useAuth } from "../context/AuthContext";
import { getApiErrorMessage } from "../lib/apiErrors";
import { formatDateDisplay, resolveDisplayTimezone } from "../lib/datetime";

const Subscription = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const appTimeZone = resolveDisplayTimezone(user?.timezone);

  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const [processingPlan, setProcessingPlan] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [paymentSuccessful, setPaymentSuccessful] = useState(false);
  // Which plan card is currently selected/highlighted (not the same as current subscription)
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  // ── Handle URL params from Stripe redirect ────────────────────────────────
  useEffect(() => {
    const paymentParam = searchParams.get("payment");
    const sessionId = searchParams.get("session_id");
    const statusParam = searchParams.get("status");

    if (statusParam === "success") {
      // Came back from successful poll — show banner, clean URL
      setPaymentSuccessful(true);
      navigate("/subscription", { replace: true });
    } else if (paymentParam === "success" && sessionId) {
      setCheckingPayment(true);
      pollPaymentStatus(sessionId);
    } else if (paymentParam === "cancelled") {
      toast.info("Checkout was cancelled — your plan was not changed.");
      navigate("/subscription", { replace: true });
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Poll Stripe verify endpoint ───────────────────────────────────────────
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      toast.error("Payment verification timed out — check your email for confirmation.");
      navigate("/subscription", { replace: true });
      return;
    }
    try {
      const { data } = await verifySubscription(sessionId);
      if (data.payment_status === "paid") {
        setCheckingPayment(false);
        // Stay on subscription page — show success banner
        navigate("/subscription?status=success", { replace: true });
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch {
      setCheckingPayment(false);
      toast.error("Error verifying payment — please contact support.");
      navigate("/subscription", { replace: true });
    }
  };

  // ── Fetch plans + current subscription ───────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [plansRes, subRes] = await Promise.all([
        getPublicPricing(),
        getSubscriptionCurrent(),
      ]);
      const fetchedPlans = plansRes.data.plans || [];
      setPlans(fetchedPlans);
      setCurrentSubscription(subRes.data);
      // Pre-select the popular plan (if user is on FREE)
      if (!subRes.data || subRes.data.plan === "FREE") {
        const popular = fetchedPlans.find((p) => p.is_popular);
        if (popular) setSelectedPlanId(popular.name);
      }
    } catch (err) {
      console.error("Error fetching subscription data:", err);
      toast.error("Failed to load subscription data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Re-fetch after successful payment to update current plan badge
  useEffect(() => {
    if (paymentSuccessful) fetchData();
  }, [paymentSuccessful]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Start Stripe Checkout ─────────────────────────────────────────────────
  const handleSubscribe = async (planId) => {
    if (planId === "FREE") {
      toast.info("You're already on the Free plan");
      return;
    }
    setProcessingPlan(planId);
    try {
      const { data } = await createSubscriptionCheckout({
        plan: planId,
        billing_period: billingPeriod,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      if (data?.bypassed) {
        setPaymentSuccessful(true);
        toast.success(data.message || "Plan activated without payment");
        await fetchData();
        return;
      }
      toast.error("Unexpected checkout response");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to start checkout"));
    } finally {
      setProcessingPlan(null);
    }
  };

  const handleManageBilling = async () => {
    setOpeningPortal(true);
    try {
      const { data } = await createBillingPortal({
        return_url: `${window.location.origin}/subscription`,
      });
      if (!data?.url) throw new Error("Missing billing portal URL");
      window.location.href = data.url;
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Unable to open billing portal"));
    } finally {
      setOpeningPortal(false);
    }
  };

  // ── Plan icon + colour helpers ────────────────────────────────────────────
  const getPlanIcon = (id) => {
    switch (id) {
      case "BASIC":      return <Zap className="w-6 h-6" />;
      case "PRO":        return <Crown className="w-6 h-6" />;
      case "ENTERPRISE": return <Sparkles className="w-6 h-6" />;
      default:           return <Shield className="w-6 h-6" />;
    }
  };

  const getPlanAccent = (id) => {
    switch (id) {
      case "BASIC":      return "text-blue-400";
      case "PRO":        return "text-purple-400";
      case "ENTERPRISE": return "text-amber-400";
      default:           return "text-zinc-400";
    }
  };

  // ── Loading / verifying state ─────────────────────────────────────────────
  if (loading || checkingPayment) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-zinc-400 text-sm">
            {checkingPayment ? "Verifying your payment…" : "Loading…"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <PlatformShell title="Subscription" titleIcon={CreditCard} backTo="/dashboard" currentKey="subscription">
      <div className="max-w-6xl mx-auto px-6 py-12">

        {/* ── Payment success banner ─────────────────────────────────────────── */}
        {paymentSuccessful && (
          <div className="mb-8 p-4 rounded-xl bg-green-500/10 border border-green-500/30 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-green-300">Subscription activated!</p>
              <p className="text-green-400/70 text-sm mt-0.5">
                Your plan is now live. Next step: connect your broker to start trading.
              </p>
            </div>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm shrink-0"
              onClick={() => navigate("/onboarding/broker-connect")}
            >
              Connect Broker
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}

        {/* ── Current plan card ──────────────────────────────────────────────── */}
        {currentSubscription && (
          <div className="mb-12">
            <Card className="bg-gradient-to-r from-zinc-900 to-zinc-800 border-zinc-700">
              <CardContent className="p-6">
                <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-xl bg-zinc-800 ${getPlanAccent(currentSubscription.plan)}`}>
                      {getPlanIcon(currentSubscription.plan)}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">
                        Current Plan: {currentSubscription.plan}
                      </h3>
                      <p className="text-zinc-400 text-sm mt-0.5">
                        {currentSubscription.status === "ACTIVE" ? (
                          <>
                            <CheckCircle2 className="w-4 h-4 inline mr-1 text-green-400" />
                            Active
                            {currentSubscription.expires_at && currentSubscription.cancel_at_period_end && (
                              <span className="ml-2">
                                · Cancels {formatDateDisplay(currentSubscription.expires_at, appTimeZone)}
                              </span>
                            )}
                            {currentSubscription.expires_at && !currentSubscription.cancel_at_period_end && (
                              <span className="ml-2">
                                · Renews {formatDateDisplay(currentSubscription.expires_at, appTimeZone)}
                              </span>
                            )}
                          </>
                        ) : (
                          <>
                            <AlertCircle className="w-4 h-4 inline mr-1 text-yellow-400" />
                            {currentSubscription.status}
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-start gap-3 sm:items-end">
                    <div className="text-left sm:text-right">
                    <div className="text-sm text-zinc-400">Limits</div>
                    <div className="text-sm">
                      {currentSubscription.max_accounts === -1 ? "Unlimited" : currentSubscription.max_accounts} accounts
                      {" · "}
                      {currentSubscription.max_strategies === -1 ? "Unlimited" : currentSubscription.max_strategies} strategies
                    </div>
                    </div>
                    {currentSubscription.billing_managed_by_stripe && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleManageBilling}
                        disabled={openingPortal}
                      >
                        {openingPortal ? <Loader2 className="w-4 h-4 animate-spin" /> : "Manage billing"}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ── Billing toggle ─────────────────────────────────────────────────── */}
        <div className="flex items-center justify-center gap-4 mb-10">
          <span className={`text-sm ${billingPeriod === "monthly" ? "text-white" : "text-zinc-500"}`}>
            Monthly
          </span>
          <Switch
            checked={billingPeriod === "yearly"}
            onCheckedChange={(v) => setBillingPeriod(v ? "yearly" : "monthly")}
            data-testid="billing-toggle"
          />
          <span className={`text-sm ${billingPeriod === "yearly" ? "text-white" : "text-zinc-500"}`}>
            Yearly
            <Badge variant="secondary" className="ml-2 text-xs bg-green-500/20 text-green-400">
              Save up to 17%
            </Badge>
          </span>
        </div>

        {/* ── Plans grid ────────────────────────────────────────────────────── */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan) => {
            const planKey = plan.name;
            const isCurrentPlan = currentSubscription?.plan === planKey;
            const isSelected = selectedPlanId === planKey && !isCurrentPlan;
            // Per-month equivalent for yearly, or monthly price
            const displayPrice =
              billingPeriod === "yearly" ? plan.price_yearly / 12 : plan.price_monthly;

            return (
              <Card
                key={planKey}
                role="button"
                tabIndex={isCurrentPlan ? -1 : 0}
                aria-pressed={isSelected}
                onClick={() => !isCurrentPlan && setSelectedPlanId(planKey)}
                onKeyDown={(e) => !isCurrentPlan && e.key === "Enter" && setSelectedPlanId(planKey)}
                className={`relative card-gloss border-0 cursor-pointer transition-all duration-200 outline-none ${
                  isCurrentPlan
                    ? "ring-2 ring-blue-500/50 cursor-default"
                    : isSelected
                    ? "ring-2 ring-purple-500/70 scale-[1.03] shadow-[0_0_28px_rgba(168,85,247,0.2)]"
                    : "hover:scale-[1.01] hover:ring-1 hover:ring-white/20"
                }`}
              >
                {planKey === "PRO" && !isCurrentPlan && !isSelected && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-purple-600 text-white">Most Popular</Badge>
                  </div>
                )}
                {isSelected && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-purple-500 text-white flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Selected
                    </Badge>
                  </div>
                )}
                {isCurrentPlan && (
                  <div className="absolute -top-3 right-4">
                    <Badge className="bg-blue-600 text-white">Current</Badge>
                  </div>
                )}

                <CardHeader className="text-center pb-4">
                  <div className={`mx-auto p-3 rounded-xl bg-zinc-800 ${getPlanAccent(planKey)} mb-3`}>
                    {getPlanIcon(planKey)}
                  </div>
                  <CardTitle className="text-xl">{plan.display_name}</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">
                      ${displayPrice > 0 ? displayPrice.toFixed(2) : "0"}
                    </span>
                    {displayPrice > 0 && (
                      <span className="text-zinc-400 text-sm">/month</span>
                    )}
                  </div>
                  {billingPeriod === "yearly" && plan.price_yearly > 0 && (
                    <div className="text-xs text-green-400 mt-1">
                      ${plan.price_yearly}/year · save {plan.yearly_discount_percent}%
                    </div>
                  )}
                  {plan.description && (
                    <CardDescription className="mt-2 text-zinc-500 text-sm">
                      {plan.description}
                    </CardDescription>
                  )}
                </CardHeader>

                <CardContent className="space-y-4">
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <span>
                        {plan.max_accounts === -1 ? "Unlimited" : plan.max_accounts} Trading Account
                        {plan.max_accounts !== 1 ? "s" : ""}
                      </span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <span>
                        {plan.max_strategies === -1 ? "Unlimited" : plan.max_strategies} Strateg
                        {plan.max_strategies !== 1 ? "ies" : "y"}
                      </span>
                    </li>
                    {plan.features.map((feat, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-zinc-400">
                        <CheckCircle2 className="w-4 h-4 text-zinc-600 flex-shrink-0" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    className={`w-full ${
                      isCurrentPlan
                        ? "bg-zinc-700 cursor-not-allowed"
                        : isSelected
                        ? "bg-purple-600 hover:bg-purple-700"
                        : planKey === "PRO"
                        ? "bg-purple-600 hover:bg-purple-700"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                    disabled={isCurrentPlan || processingPlan !== null}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSubscribe(planKey);
                    }}
                    data-testid={`subscribe-${planKey.toLowerCase()}-btn`}
                  >
                    {processingPlan === planKey ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : isCurrentPlan ? (
                      "Current Plan"
                    ) : isSelected ? (
                      <>Continue <ArrowRight className="w-4 h-4 ml-1" /></>
                    ) : planKey === "FREE" ? (
                      "Get Started"
                    ) : (
                      plan.cta || "Upgrade Now"
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* ── Broker connect CTA (paid plan, not just upgraded) ─────────────── */}
        {currentSubscription && currentSubscription.plan !== "FREE" && !paymentSuccessful && (
          <div className="text-center mt-10">
            <p className="text-zinc-400 text-sm mb-4">
              You're on the <strong>{currentSubscription.plan}</strong> plan.
            </p>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white px-8"
              onClick={() => navigate("/onboarding/broker-connect")}
            >
              Connect Broker →
            </Button>
          </div>
        )}

        {/* ── Stripe badge ──────────────────────────────────────────────────── */}
        <div className="mt-16 text-center">
          <p className="text-zinc-500 text-sm mb-4">Secure payments powered by</p>
          <div className="flex items-center justify-center gap-6">
            <div className="flex items-center gap-2 text-zinc-400">
              <CreditCard className="w-5 h-5" />
              <span>Stripe</span>
            </div>
          </div>
          <p className="text-zinc-600 text-xs mt-4">
            All paid plans include a 14-day money-back guarantee.
          </p>
        </div>
      </div>
    </PlatformShell>
  );
};

export default Subscription;
